import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { WholesaleDuplicateCandidate } from "../../../shared/contracts.js";
import { fieldErrorsOf, orderMutationErrorMessage } from "../../api/caseMutations.js";
import {
  useCreateWholesaleOrder,
  wholesaleDuplicateCandidatesOf,
  type WholesaleMutationResultDto,
} from "../../api/wholesaleQueries.js";
import { Button } from "../../ui/Button.js";
import { DuplicateWarningDialog } from "../cases/order-form/DuplicateWarningDialog.js";
import { focusFirstInvalid, OrderFormSection } from "../cases/order-form/OrderForm.js";
import {
  clearChangedLineMarks,
  MasterEtaField,
  newLineValues,
  PartLinesGrid,
  withMasterEta,
  withoutBlankTrailingLines,
} from "../cases/order-form/PartLinesGrid.js";
import { UnsavedChangesGuard } from "../cases/order-form/UnsavedChangesGuard.js";
import { CustomerPicker } from "./CustomerPicker.js";
import { DEFAULT_WHOLESALE_VIEW, WHOLESALE_PATH } from "./presentation.js";
import { WholesaleShell } from "./WholesaleShell.js";
import {
  emptyWholesaleForm,
  isPristineWholesaleForm,
  NO_WHOLESALE_ERRORS,
  toCreateWholesaleOrderInput,
  validateWholesaleForm,
  WholesaleDetailsFields,
  wholesaleDuplicateLinks,
  wholesaleErrorsFromServer,
  type WholesaleFormErrors,
  type WholesaleFormValues,
  type WholesaleHeaderValues,
} from "./WholesaleOrderForm.js";
import { wholesaleViewPath } from "./WholesaleSidebar.js";

/**
 * `/wholesale/new` — the wholesale order form (spec §4.3), built for
 * 50–100 orders a day: the customer type-ahead with an inline Add
 * customer, the reference and R&R number, "ETA for all parts", the order
 * note and the shared parts grid (catalog auto-fill, Enter adds a row, a
 * pasted `part<TAB>qty` list fills rows), ending in Cancel and Create
 * order.
 *
 * Validation runs in the browser on submit and marks each field inline;
 * the server validates again and its field errors land in the same
 * places. One successful `POST /api/wholesale/orders` creates exactly one
 * order and the page opens it on the Open list. A 409
 * `DUPLICATE_WHOLESALE_ORDER` opens the duplicate warning with the
 * matching WS orders; proceeding resubmits the unchanged input with the
 * override reason. Leaving with something typed asks first; Cancel is an
 * explicit choice. The page is Parts' own — the shell sends any other
 * role home.
 */

/** Where a created order is shown: selected on the Open list. */
export function createdWholesaleOrderPath(orderId: string): string {
  return `${WHOLESALE_PATH}?view=${DEFAULT_WHOLESALE_VIEW}&order=${encodeURIComponent(orderId)}`;
}

export function NewWholesaleOrderPage() {
  return (
    <WholesaleShell page activeDestination="new_order">
      <NewWholesaleOrderForm />
    </WholesaleShell>
  );
}

function NewWholesaleOrderForm() {
  const navigate = useNavigate();
  const titleId = useId();
  const idPrefix = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const [values, setValues] = useState<WholesaleFormValues>(emptyWholesaleForm);
  const [errors, setErrors] = useState<WholesaleFormErrors>(NO_WHOLESALE_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<readonly WholesaleDuplicateCandidate[] | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);

  const create = useCreateWholesaleOrder();

  const valuesRef = useRef(values);
  valuesRef.current = values;
  const leavingRef = useRef(false);
  const hasUnsavedWork = useCallback(() => !leavingRef.current && !isPristineWholesaleForm(valuesRef.current), []);

  useEffect(() => {
    if (focusRequest > 0) focusFirstInvalid(formRef.current);
  }, [focusRequest]);

  const updateHeader = (patch: Partial<WholesaleHeaderValues>) => {
    setValues((current) => ({
      ...current,
      ...patch,
      // "ETA for all parts" fills the blank lines as it is typed (spec §6.5).
      ...(patch.masterEta !== undefined ? { partLines: withMasterEta(current.partLines, patch.masterEta) } : {}),
    }));
    setErrors((current) => {
      const fields = { ...current.fields };
      let changed = false;
      for (const key of Object.keys(patch) as (keyof WholesaleHeaderValues)[]) {
        if (fields[key]) {
          delete fields[key];
          changed = true;
        }
      }
      return changed ? { ...current, fields } : current;
    });
    if (formError) setFormError(null);
  };

  const updateLines = (partLines: WholesaleFormValues["partLines"]) => {
    const previousLines = values.partLines;
    setValues((current) => ({ ...current, partLines: withMasterEta(partLines, current.masterEta) }));
    setErrors((current) => {
      const lines = clearChangedLineMarks(current.lines, previousLines, partLines);
      return lines === current.lines ? current : { ...current, lines };
    });
  };

  const finish = (result: WholesaleMutationResultDto) => {
    leavingRef.current = true;
    navigate(createdWholesaleOrderPath(result.orderId));
  };

  // What a submit sends: the form less the untouched rows at the end (the
  // row Enter adds after the last part, spec §4.3), so that row is never refused.
  const submitted = (): WholesaleFormValues => ({ ...values, partLines: withoutBlankTrailingLines(values.partLines) });

  const showServerError = (error: unknown, input: WholesaleFormValues) => {
    const fieldErrors = fieldErrorsOf(error);
    if (fieldErrors) {
      const mapped = wholesaleErrorsFromServer(fieldErrors, input);
      setErrors(mapped.errors);
      setFormError(mapped.unmapped.length > 0 ? mapped.unmapped.join(" ") : null);
      if (!mapped.errors.isValid) setFocusRequest((n) => n + 1);
      else if (mapped.unmapped.length === 0) setFormError(orderMutationErrorMessage(error));
      return;
    }
    setFormError(orderMutationErrorMessage(error));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // One successful creation is one order: a click that lands after the
    // response and before the page has moved on must not create another.
    if (create.isPending || leavingRef.current) return;
    const input = submitted();
    const validation = validateWholesaleForm(input);
    setErrors(validation);
    setFormError(null);
    if (!validation.isValid) {
      setFocusRequest((n) => n + 1);
      return;
    }
    create.mutate(toCreateWholesaleOrderInput(input), {
      onSuccess: finish,
      onError: (error) => {
        const candidates = wholesaleDuplicateCandidatesOf(error);
        if (candidates) {
          setDuplicate(candidates);
          setOverrideError(null);
          return;
        }
        showServerError(error, input);
      },
    });
  };

  const proceedWithOverride = (reason: string) => {
    if (!duplicate || create.isPending || leavingRef.current) return;
    const input = submitted();
    setOverrideError(null);
    create.mutate(toCreateWholesaleOrderInput(input, { duplicateOverrideReason: reason }), {
      onSuccess: finish,
      onError: (error) => {
        const fieldErrors = fieldErrorsOf(error);
        const reasonMessage = fieldErrors?.["duplicateOverrideReason"]?.find((text) => text.length > 0);
        if (reasonMessage) {
          setOverrideError(reasonMessage);
          return;
        }
        if (wholesaleDuplicateCandidatesOf(error)) {
          setOverrideError("The server still sees a duplicate. Check the reason and try again.");
          return;
        }
        setDuplicate(null);
        showServerError(error, input);
      },
    });
  };

  const cancel = () => {
    leavingRef.current = true;
    navigate(wholesaleViewPath({ view: DEFAULT_WHOLESALE_VIEW, orderId: null, query: "", customerId: null }, DEFAULT_WHOLESALE_VIEW));
  };

  const busy = create.isPending;

  return (
    <div className="order-page">
      <header className="order-page__header">
        <h1 id={titleId} className="order-page__title">
          New wholesale order
        </h1>
        <p className="order-page__lead">
          Pick the customer and enter every part ordered. Nothing is saved until you create the order.
        </p>
      </header>

      <form ref={formRef} className="order-form" aria-labelledby={titleId} noValidate onSubmit={submit}>
        <OrderFormSection title="Customer">
          <div className="order-form__grid">
            <CustomerPicker
              id={`${idPrefix}-customer`}
              value={values.customer}
              onChange={(customer) => updateHeader({ customer })}
              error={errors.fields.customer ?? null}
              disabled={busy}
            />
          </div>
        </OrderFormSection>

        <OrderFormSection title="Order details">
          <WholesaleDetailsFields values={values} errors={errors.fields} onChange={updateHeader} disabled={busy} idPrefix={idPrefix} />
        </OrderFormSection>

        <OrderFormSection
          title="Parts ordered"
          description="Add every part for this order. Enter on the last line adds a row; paste a list of part number, tab, quantity to fill rows."
        >
          <MasterEtaField
            value={values.masterEta}
            error={errors.fields.masterEta}
            onChange={(masterEta) => updateHeader({ masterEta })}
            disabled={busy}
            idPrefix={idPrefix}
          />
          <PartLinesGrid
            lines={values.partLines}
            errors={errors.lines}
            onChange={updateLines}
            createLine={() => newLineValues(values.masterEta)}
            disabled={busy}
            idPrefix={idPrefix}
          />
        </OrderFormSection>

        {formError ? (
          <p role="alert" className="form-alert order-form__alert">
            {formError}
          </p>
        ) : null}

        <div className="order-form__actions">
          <Button variant="secondary" onClick={cancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" busy={busy} disabled={busy}>
            Create order
          </Button>
        </div>
      </form>

      <UnsavedChangesGuard when={hasUnsavedWork} />

      <DuplicateWarningDialog
        open={duplicate !== null}
        links={wholesaleDuplicateLinks(duplicate ?? [])}
        onClose={() => setDuplicate(null)}
        onProceed={proceedWithOverride}
        pending={busy}
        error={overrideError}
        proceedLabel="Create anyway"
      />
    </div>
  );
}
